'use client'

import posthog from 'posthog-js'
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import { hasAnalyticsConsent } from '@/lib/cookie-consent'
import {
	clearSignedOutFlag,
	extractAndSaveAuthFromUrl,
	isUserDataValid,
	parseUserDataCookie,
	redirectToLogin,
	refreshJWTTokens,
	signOutJWT,
	type UserDataCookie,
	verifyJWTWithAuthService,
	wasSignedOut,
} from '@/lib/jwt-auth'
import { isInternalAnalyticsUser } from '../../../shared-types/posthog-privacy'

/**
 * Auth context for dojo. Mirrors catalog-frontend's `AuthProvider` —
 * one cookie read + one Genkan verify per page (not per component),
 * which fixes the parallel-getSession storm the legacy
 * `@/hooks/use-auth` produced when sidebar + user-menu + page each
 * mounted their own Supabase subscription.
 *
 * The shape of `useAuth()` is intentionally backward-compatible with
 * the previous hook so call-sites (page.tsx, user-menu.tsx, …) don't
 * need to change: `{ user: { id, email, role } | null, loading,
 * signOut, isAuthenticated }`.
 */

export interface AuthUser {
	id: string
	email: string
	role: string
	permissions: string[]
}

interface AuthContextValue {
	user: AuthUser | null
	loading: boolean
	error: string | null
	isAuthenticated: boolean
	signOut: () => Promise<void>
	refresh: () => Promise<void>
	login: (returnUrl?: string) => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

const toAuthUser = (data: UserDataCookie | null): AuthUser | null =>
	data
		? {
				id: data.userId,
				email: data.email,
				role: data.role,
				permissions: data.permissions,
			}
		: null

export function AuthProvider({ children }: { children: ReactNode }) {
	const [userData, setUserData] = useState<UserDataCookie | null>(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)

	const isAuthenticated = isUserDataValid(userData)
	const user = useMemo(() => toAuthUser(userData), [userData])

	const readUserFromCookie = useCallback((): UserDataCookie | null => {
		try {
			const parsed = parseUserDataCookie()
			if (isUserDataValid(parsed)) return parsed
		} catch (e) {
			console.error('[AUTH] Failed to parse user cookie:', e)
		}
		return null
	}, [])

	const loadAndApplyUser = useCallback((): UserDataCookie | null => {
		const next = readUserFromCookie()
		setUserData(next)
		if (next) setError(null)
		return next
	}, [readUserFromCookie])

	const verifyWithAuthService = useCallback(async (backgroundMode = false) => {
		if (wasSignedOut()) {
			if (!backgroundMode) setUserData(null)
			return null
		}
		try {
			setError(null)
			const jwtPayload = await verifyJWTWithAuthService()
			if (jwtPayload) {
				const next: UserDataCookie = {
					userId: jwtPayload.userId,
					email: jwtPayload.email,
					role: jwtPayload.role,
					permissions: jwtPayload.permissions,
					expiresAt: jwtPayload.exp * 1000,
				}
				setUserData(next)
				return next
			}
			if (!backgroundMode) setUserData(null)
		} catch (e) {
			console.error('[AUTH] JWT verification failed:', e)
			if (!backgroundMode) {
				setError(e instanceof Error ? e.message : 'Authentication failed')
				setUserData(null)
			}
		}
		return null
	}, [])

	const signOut = useCallback(async () => {
		try {
			setError(null)
			setLoading(true)
			// Fail closed: only treat the user as signed out once Genkan confirms it
			// cleared the HttpOnly extenshi-* cookies (DELETE /auth/logout → 2xx).
			// signOutJWT can only wipe the non-HttpOnly USER_DATA cookie from JS, so
			// if the cross-origin call fails the auth/refresh JWT survives — showing a
			// "signed out" UI then would leave a zombie session that keeps
			// authenticating. Keep the session and surface a retry instead.
			const cleared = await signOutJWT()
			if (!cleared) {
				clearSignedOutFlag()
				setError('Sign out failed — please try again')
				return
			}
			setUserData(null)
			window.location.href = process.env.NEXT_PUBLIC_GENKAN_URL || 'http://localhost:3005'
		} catch (e) {
			console.error('Sign out error:', e)
			clearSignedOutFlag()
			setError(e instanceof Error ? e.message : 'Sign out failed')
		} finally {
			setLoading(false)
		}
	}, [])

	const refresh = useCallback(async () => {
		if (wasSignedOut()) return
		try {
			setError(null)
			setLoading(true)

			// Fast path: cookie still has > 10 min of life — fall through to
			// rotate when within the 5-minute checkTokenExpiry window.
			const cookieUser = loadAndApplyUser()
			const tenMinutesInMs = 10 * 60 * 1000
			if (cookieUser && cookieUser.expiresAt - Date.now() > tenMinutesInMs) {
				return
			}

			const success = await refreshJWTTokens()
			if (success) {
				const refreshed = loadAndApplyUser()
				if (refreshed) return
			}

			const verified = await verifyWithAuthService(false)
			if (!verified) {
				setUserData(null)
				setError('Session expired')
			}
		} catch (e) {
			console.error('Refresh error:', e)
			setError(e instanceof Error ? e.message : 'Failed to refresh session')
			setUserData(null)
		} finally {
			setLoading(false)
		}
	}, [loadAndApplyUser, verifyWithAuthService])

	const login = useCallback((returnUrl?: string) => {
		clearSignedOutFlag()
		redirectToLogin(returnUrl)
	}, [])

	const lastIdentifiedRef = useRef<string | null>(null)
	useEffect(() => {
		function syncAnalyticsIdentity() {
			if (!hasAnalyticsConsent() || !posthog.__loaded) {
				lastIdentifiedRef.current = null
				return
			}
			if (user?.id) {
				const isInternal = isInternalAnalyticsUser(user)
				posthog.register({ is_internal: isInternal })
				if (user.id !== lastIdentifiedRef.current) {
					posthog.identify(user.id, { email: user.email, role: user.role, is_internal: isInternal })
					lastIdentifiedRef.current = user.id
				}
			} else if (lastIdentifiedRef.current) {
				posthog.reset()
				lastIdentifiedRef.current = null
			}
		}
		syncAnalyticsIdentity()
		window.addEventListener('cookie-consent-updated', syncAnalyticsIdentity)
		window.addEventListener('posthog-initialized', syncAnalyticsIdentity)
		return () => {
			window.removeEventListener('cookie-consent-updated', syncAnalyticsIdentity)
			window.removeEventListener('posthog-initialized', syncAnalyticsIdentity)
		}
	}, [user])

	const userRef = useRef<UserDataCookie | null>(null)
	const refreshRef = useRef(refresh)
	useEffect(() => {
		userRef.current = userData
	}, [userData])
	useEffect(() => {
		refreshRef.current = refresh
	}, [refresh])

	const checkTokenExpiry = useCallback(() => {
		const current = userRef.current
		if (!current) return
		const fiveMinutesInMs = 5 * 60 * 1000
		if (current.expiresAt - Date.now() <= fiveMinutesInMs) {
			refreshRef.current()
		}
	}, [])

	// One-shot init: hydrate from cookie/URL, then confirm with Genkan in
	// the background. No parallel verify() per consumer — the whole tree
	// reads from this single state.
	useEffect(() => {
		let mounted = true
		const initAuth = async () => {
			try {
				setLoading(true)
				const extractedFromUrl = extractAndSaveAuthFromUrl()
				if (extractedFromUrl) {
					clearSignedOutFlag()
					const fresh = readUserFromCookie()
					if (fresh) {
						setUserData(fresh)
						setLoading(false)
						return
					}
				}
				const cookieUser = readUserFromCookie()
				if (cookieUser) {
					setUserData(cookieUser)
					setLoading(false)
					verifyWithAuthService(true)
				} else {
					setUserData(null)
				}
			} finally {
				if (mounted) setLoading(false)
			}
		}
		initAuth()
		return () => {
			mounted = false
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [readUserFromCookie, verifyWithAuthService])

	// Periodic cookie-state sync + cross-tab + focus listeners.
	useEffect(() => {
		let mounted = true
		const interval = setInterval(() => {
			if (!mounted || wasSignedOut()) return
			const updated = loadAndApplyUser()
			if (updated) checkTokenExpiry()
		}, 30000)

		const handleStorageChange = (e: StorageEvent) => {
			if (e.key?.startsWith('extenshi_auth_event_')) {
				setTimeout(() => {
					if (mounted) loadAndApplyUser()
				}, 100)
			}
		}
		const handleFocus = () => {
			if (mounted && !wasSignedOut()) loadAndApplyUser()
		}
		window.addEventListener('storage', handleStorageChange)
		window.addEventListener('focus', handleFocus)

		return () => {
			mounted = false
			clearInterval(interval)
			window.removeEventListener('storage', handleStorageChange)
			window.removeEventListener('focus', handleFocus)
		}
	}, [loadAndApplyUser, checkTokenExpiry])

	return (
		<AuthContext.Provider
			value={{
				user,
				loading,
				error,
				isAuthenticated,
				signOut,
				refresh,
				login,
			}}
		>
			{children}
		</AuthContext.Provider>
	)
}

export function useAuth(): AuthContextValue {
	const ctx = useContext(AuthContext)
	if (!ctx) {
		throw new Error('useAuth must be used within an AuthProvider')
	}
	return ctx
}
